import {
  BaseEdge,
  Edge,
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  XYPosition,
} from "@xyflow/react";
import React, { useMemo, useRef, useEffect, useState } from "react";
import { useSequenceCanvasLod } from "./SequenceCanvasLod";

// Define your custom edge data type
type CustomEdgeData = {
  width?: number;
  color?: string;
  sourceColor?: string;
  targetColor?: string;
  isTemporary?: boolean;
  /** The edge or one of its nodes is selected: draw it in full blue instead of the muted default. */
  highlighted?: boolean;
  onEdgeClick?: (edgeId: string, clientX: number, clientY: number) => void;
  showInsertButton?: boolean;
  sourceHandle?: string | null;
  onEdgePathGeometryChange?: (edgeId: string, points: XYPosition[]) => void;
  onEdgePathGeometryRemove?: (edgeId: string) => void;
};

// Use it in your edge type
type CustomEdge = Edge<CustomEdgeData>;

export default function SequenceCustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  selected,
}: EdgeProps<CustomEdge>) {
  const lod = useSequenceCanvasLod();
  const [edgePath] =
    lod === "overview"
      ? getSmoothStepPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
          borderRadius: 0,
        })
      : getBezierPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
        });

  const pathRef = useRef<SVGPathElement>(null);
  const [midpoint, setMidpoint] = useState<{ x: number; y: number } | null>(
    null
  );

  // Calculate midpoint along the bezier curve
  useEffect(() => {
    if (pathRef.current) {
      const pathLength = pathRef.current.getTotalLength();
      // A path built from a NaN coordinate is empty, and sampling it throws, which takes
      // the whole editor down.
      if (!(pathLength > 0)) return;
      const midPoint = pathRef.current.getPointAtLength(pathLength / 2);
      setMidpoint({ x: midPoint.x, y: midPoint.y });

      if (data?.onEdgePathGeometryChange) {
        const sampleCount = Math.max(12, Math.ceil(pathLength / 24));
        const sampledPoints: XYPosition[] = [];
        for (let index = 0; index <= sampleCount; index += 1) {
          const point = pathRef.current.getPointAtLength(
            (pathLength * index) / sampleCount,
          );
          sampledPoints.push({ x: point.x, y: point.y });
        }
        data.onEdgePathGeometryChange(id, sampledPoints);
      }
    }
  }, [data, edgePath, id]);

  useEffect(() => {
    return () => {
      data?.onEdgePathGeometryRemove?.(id);
    };
  }, [data, id]);

  const handleButtonClick = (e: React.MouseEvent<SVGCircleElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (data?.onEdgeClick) {
      // Use the event's screen coordinates directly
      data.onEdgeClick(id, e.clientX, e.clientY);
    }
  };

  const handleButtonMouseDown = (e: React.MouseEvent<SVGCircleElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Prevent ReactFlow from handling this event
  };

  // Don't show button for temporary edges, only show when exactly one edge is selected and no nodes
  const showButton =
    lod !== "overview" &&
    !data?.isTemporary &&
    data?.onEdgeClick &&
    data?.showInsertButton === true;

  // Default connectors are a muted blue (70%, mixed against the surface for dark mode);
  // the selected edge or a selected node's connections, and edges being drawn, are full blue.
  // Custom node colours and true/false path colours are unchanged.
  const defaultColor =
    data?.highlighted || selected || data?.isTemporary
      ? "#3AC4FF"
      : "color-mix(in srgb, #3AC4FF 70%, var(--pi-surface))";
  const edgeColor = data?.color ?? defaultColor;
  const sourceColor = data?.sourceColor ?? defaultColor;
  const targetColor = data?.targetColor ?? defaultColor;
  const baseStrokeWidth =
    lod === "overview" ? Math.max(data?.width ?? 2, 4) : data?.width ?? 2;
  const useGradient =
    sourceColor &&
    targetColor &&
    sourceColor !== targetColor &&
    !data?.isTemporary;

  // Unique gradient id safe for SVG (edge id may contain chars like '-')
  const gradientId = `edge-gradient-${id.replace(/[^a-zA-Z0-9-_]/g, "_")}`;

  return (
    <g>
      <style>{`
        .edge-insert-group { transition: transform 150ms ease; transform-box: fill-box; transform-origin: center; }
        .edge-insert-group:hover { transform: scale(1.3); }
      `}</style>
      {useGradient && (
        <defs>
          <linearGradient
            id={gradientId}
            x1={sourceX}
            y1={sourceY}
            x2={targetX}
            y2={targetY}
            gradientUnits="userSpaceOnUse"
          >
            {/* Colours go through style: the muted default uses var(), which
                SVG presentation attributes don't resolve (it falls back to black). */}
            <stop offset="0%" style={{ stopColor: sourceColor }} />
            <stop offset="100%" style={{ stopColor: targetColor }} />
          </linearGradient>
        </defs>
      )}
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          strokeWidth: selected
            ? baseStrokeWidth + 1
            : data?.highlighted
              ? baseStrokeWidth + 0.5
              : baseStrokeWidth,
          stroke: useGradient ? `url(#${gradientId})` : edgeColor,
          strokeDasharray: data?.isTemporary ? "5,5" : undefined,
          opacity: data?.isTemporary ? 0.7 : 1,
        }}
      />
      {/* Hidden path for calculating midpoint */}
      <path
        ref={pathRef}
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth="20"
        style={{ pointerEvents: "none", visibility: "hidden" }}
      />
      {/* Button at midpoint */}
      {showButton && midpoint && (
        <g transform={`translate(${midpoint.x}, ${midpoint.y})`}>
          <g className="edge-insert-group">
            <circle
              r="12"
              strokeWidth="2"
              style={{
                fill: "var(--pi-surface)",
                stroke: useGradient ? sourceColor : edgeColor,
                cursor: "pointer",
                pointerEvents: "all",
              }}
              onClick={handleButtonClick}
              onMouseDown={handleButtonMouseDown}
              className="edge-insert-button"
            />
            <text
              x="0"
              y="0"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="16"
              fontWeight="bold"
              style={{
                fill: useGradient ? sourceColor : edgeColor,
                cursor: "pointer",
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              +
            </text>
          </g>
        </g>
      )}
    </g>
  );
}
